import { capitalize, getNameParts } from './order-helpers'
import type { BuyerInfo } from './orders-store'
import type { YMarketOrder } from './types'

// Suffixes that strongly indicate a Russian/CIS last name
const LAST_SUFFIXES = [
  'ский', 'ская', 'цкий', 'цкая', 'дской', 'дская',
  'швили', 'дзе', 'иани', 'ури', 'ели', 'ани',
  'оглу', 'заде', 'задэ', 'лы',
  'чук', 'юк',
  'ских', 'овых',
]

// Short suffixes checked separately (to avoid false positives on short words)
const LAST_SUFFIXES_SHORT = ['ов', 'ова', 'ев', 'ева', 'ин', 'ина', 'ых', 'их', 'ой', 'ко']

// Patronymic suffixes — near-100% reliable
const PATRONYMIC_SUFFIXES = ['ович', 'евич', 'ьич', 'овна', 'евна', 'инична', 'ична']

// Top Russian first names (nominative, lowercase)
const FIRST_NAMES = new Set([
  // Male
  'александр', 'алексей', 'андрей', 'антон', 'артём', 'артем', 'борис', 'вадим',
  'валентин', 'валерий', 'василий', 'виктор', 'виталий', 'владимир', 'владислав',
  'вячеслав', 'геннадий', 'георгий', 'григорий', 'даниил', 'данила', 'данил',
  'денис', 'дмитрий', 'евгений', 'иван', 'игорь', 'илья', 'кирилл', 'константин',
  'леонид', 'максим', 'михаил', 'никита', 'николай', 'олег', 'павел', 'пётр',
  'петр', 'роман', 'руслан', 'сергей', 'степан', 'тимофей', 'тимур', 'фёдор',
  'федор', 'филипп', 'юрий', 'яков', 'ярослав', 'матвей', 'глеб', 'арсений',
  'богдан', 'егор', 'захар', 'лев', 'марк', 'мирон', 'платон', 'семён', 'семен',
  'станислав', 'тихон', 'эдуард', 'элли', 'эмиль', 'рустам', 'азиз', 'тигран',
  'армен', 'карен', 'гарик', 'арман', 'амир', 'рамиль', 'ришат', 'айдар',
  'ринат', 'ильдар', 'азат', 'булат', 'марат', 'рафаэль', 'нурлан', 'бекзод',
  // Female
  'александра', 'алёна', 'алена', 'алина', 'алла', 'альбина', 'анастасия', 'анна', 'валентина',
  'валерия', 'вера', 'вероника', 'виктория', 'галина', 'дарья', 'диана',
  'екатерина', 'елена', 'жанна', 'зинаида', 'зоя', 'инна', 'ирина', 'карина',
  'кристина', 'ксения', 'лариса', 'лидия', 'людмила', 'маргарита', 'марина',
  'мария', 'надежда', 'наталья', 'нина', 'оксана', 'ольга', 'полина', 'светлана',
  'снежана', 'тамара', 'татьяна', 'ульяна', 'юлия', 'яна', 'юлиана', 'элина',
  'эльвира', 'эвелина', 'sofia', 'софия', 'милана', 'виолетта', 'лиза',
  'елизавета', 'арина', 'стефания', 'антонина', 'василиса', 'евгения', 'инесса',
  'регина', 'рита', 'эмилия', 'злата', 'агния', 'аделина', 'азиза', 'айгуль',
  'гузель', 'лейла', 'нигяр', 'нилуфар', 'нино', 'тамта', 'манана', 'нана',
  // Attested names with surname-like endings; sources:
  // forge/customer_messaging/greeting-names.md.
  'ангелина', 'дарина', 'амина', 'залина', 'зарина', 'мадина', 'ралина', 'ясмина',
  // Common short forms
  'саша', 'катя', 'маша', 'настя', 'таня', 'наташа', 'лена', 'оля', 'юля',
  'аня', 'соня', 'даша', 'поля', 'вика', 'женя', 'лера', 'рита', 'мила',
  'коля', 'ваня', 'витя', 'митя', 'серёжа', 'серёга', 'паша', 'дима', 'гриша',
  'слава', 'стёпа', 'тёма', 'федя', 'лёша', 'сеня', 'лёня', 'ромка', 'ромик',
])

function isPatronymic(s: string): boolean {
  const lower = s.toLowerCase()
  return PATRONYMIC_SUFFIXES.some((suf) => lower.endsWith(suf))
}

function lastNameScore(s: string): number {
  const lower = s.toLowerCase()
  if (LAST_SUFFIXES.some((suf) => lower.endsWith(suf))) return 0.9
  if (LAST_SUFFIXES_SHORT.some((suf) => lower.endsWith(suf) && lower.length > suf.length + 1)) return 0.8
  return 0
}

function firstNameScore(s: string): number {
  return FIRST_NAMES.has(s.toLowerCase()) ? 0.95 : 0
}

export interface NamePartScore {
  part: string
  firstName: number
  lastName: number
  middleName: number
}

export function scoreNameParts(parts: string[]): NamePartScore[] {
  return parts.map((part) => {
    if (isPatronymic(part)) {
      return { part, firstName: 0, lastName: 0.05, middleName: 0.99 }
    }
    const fn = firstNameScore(part)
    const ln = lastNameScore(part)
    // If neither matched, lean on position heuristic applied externally
    return { part, firstName: fn, lastName: ln, middleName: 0 }
  })
}

// Returns the index of the part most likely to be the first name (for greeting)
export function guessGreetingIndex(parts: string[]): number {
  if (parts.length === 0) return 0
  if (parts.length === 1) return 0

  const scores = scoreNameParts(parts)

  // Exclude patronymics from consideration
  const candidates = scores
    .map((s, i) => ({ ...s, i }))
    .filter((s) => s.middleName < 0.5)

  if (candidates.length === 0) return parts.length - 1

  // Find the candidate with highest firstName score
  const bestFirst = candidates.reduce((best, c) => (c.firstName > best.firstName ? c : best))

  if (bestFirst.firstName >= 0.5) return bestFirst.i

  // No name in list — check if any part is a recognized surname
  const mostLikelyLast = candidates.reduce((best, c) => (c.lastName > best.lastName ? c : best))

  if (mostLikelyLast.lastName >= 0.5) {
    // Clear surname found — pick the other candidate
    const other = candidates.find((c) => c.i !== mostLikelyLast.i)
    if (other) return other.i
  }

  // Truly unknown — assume "Фамилия Имя" format, prefer second part
  return candidates.find((c) => c.i === 1)?.i ?? candidates[candidates.length - 1].i
}

// Returns the capitalized greeting name from parts, using heuristics when idx is null
export function resolveGreetingName(parts: string[], idx: number | null): string {
  if (parts.length === 0) return ''
  const i = idx !== null && idx >= 0 && idx < parts.length
    ? idx
    : guessGreetingIndex(parts)
  return capitalize(parts[i])
}

// Mirrors forge/customer_messaging/name_heuristics.py is_greetable_token.
export function isGreetableToken(part: string): boolean {
  if (isPatronymic(part)) return false
  return firstNameScore(part) >= 0.5 || lastNameScore(part) < 0.5
}

/** Name parts for the greeting picker; a provider "first name" that reads as a surname is not trusted alone. */
export function orderGreetingNameParts(order: YMarketOrder, buyer: BuyerInfo | null): string[] {
  const firstName = order.customer?.firstName?.trim()
  if (!firstName) return getNameParts(buyer)
  if (isGreetableToken(firstName)) return [firstName]
  const displayParts = order.customer?.displayName?.trim().split(/\s+/) ?? []
  if (displayParts.length > 1) return displayParts
  const buyerParts = getNameParts(buyer)
  return buyerParts.length > 0 ? buyerParts : [firstName]
}
